import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'

// =====================
// Workflow Enums
// =====================

export const WorkflowStatusEnum = z.enum([
  'DRAFT',
  'ACTIVE',
  'PAUSED',
  'ARCHIVED',
])
export type WorkflowStatus = z.infer<typeof WorkflowStatusEnum>

export const WorkflowRunStatusEnum = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
])
export type WorkflowRunStatus = z.infer<typeof WorkflowRunStatusEnum>

export const WorkflowRunStepStatusEnum = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
])
export type WorkflowRunStepStatus = z.infer<typeof WorkflowRunStepStatusEnum>

export const WorkflowRunStepTypeEnum = z.enum(['TRIGGER', 'ACTION'])
export type WorkflowRunStepType = z.infer<typeof WorkflowRunStepTypeEnum>

export const WorkflowLogLevelEnum = z.enum(['INFO', 'WARN', 'ERROR'])
export type WorkflowLogLevel = z.infer<typeof WorkflowLogLevelEnum>

export const WorkflowTriggerTypeEnum = z.enum([
  'MODULE_EVENT',
  'SCHEDULED',
  'WEBHOOK',
  'FORM_SUBMISSION',
  'STAGE_CHANGE',
  'STATUS_CHANGE',
])
export type WorkflowTriggerType = z.infer<typeof WorkflowTriggerTypeEnum>

export const WorkflowActionTypeEnum = z.enum([
  'FIND_RECORD',
  'CREATE_RECORD',
  'UPDATE_RECORD',
  'SEND_NOTIFICATION',
  'SEND_EMAIL',
  'SEND_SMS',
  'ASSIGN_OWNER',
  'MOVE_STAGE',
  'POST_NOTE',
  'CREATE_TASK',
  'CREATE_INVOICE',
  'UPDATE_INVENTORY',
  'LOG_TIME',
  'DELEGATE_TO_AI_AGENT',
])
export type WorkflowActionType = z.infer<typeof WorkflowActionTypeEnum>

export const WorkflowActionTriggerTimeEnum = z.enum([
  'ON_TRIGGER',
  'DELAYED',
  'SCHEDULED',
])
export type WorkflowActionTriggerTime = z.infer<
  typeof WorkflowActionTriggerTimeEnum
>

export const WorkflowConditionOperatorEnum = z.enum([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
  'in',
  'not_in',
  'between',
  'not_between',
  'before',
  'after',
  'on_or_before',
  'on_or_after',
  'is_empty',
  'is_not_empty',
  'is_true',
  'is_false',
])
export type WorkflowConditionOperator = z.infer<
  typeof WorkflowConditionOperatorEnum
>

export const WorkflowConditionLogicEnum = z.enum(['all', 'any'])
export type WorkflowConditionLogic = z.infer<typeof WorkflowConditionLogicEnum>

// =====================
// Workflow Conditions
// =====================

export const WorkflowConditionValueTypeEnum = z.enum([
  'string',
  'number',
  'boolean',
  'date',
  'datetime',
  'array',
  'json',
])
export type WorkflowConditionValueType = z.infer<
  typeof WorkflowConditionValueTypeEnum
>

export const WorkflowConditionLookupSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum),
  idPath: z.string().optional(),
  id: z.string().uuid().optional(),
})
export type WorkflowConditionLookup = z.infer<
  typeof WorkflowConditionLookupSchema
>

export const WorkflowConditionSchema = z.object({
  field: z.string(),
  operator: WorkflowConditionOperatorEnum,
  value: z.unknown().optional(),
  valueType: WorkflowConditionValueTypeEnum.optional(),
  lookup: WorkflowConditionLookupSchema.optional(),
})
export type WorkflowCondition = z.infer<typeof WorkflowConditionSchema>

// Define the recursive type explicitly for proper inference
export interface WorkflowConditionGroup {
  logic: 'all' | 'any'
  conditions: WorkflowCondition[]
  groups?: WorkflowConditionGroup[]
  label?: string
}

// For recursive Zod schemas, we need to cast the schema to satisfy TypeScript
// while still getting runtime validation
export const WorkflowConditionGroupSchema: z.ZodType<WorkflowConditionGroup> =
  z.object({
    logic: WorkflowConditionLogicEnum,
    conditions: z.array(WorkflowConditionSchema),
    groups: z.lazy(() => z.array(WorkflowConditionGroupSchema)).optional(),
    label: z.string().optional(),
  }) as z.ZodType<WorkflowConditionGroup>

// =====================
// Workflow Settings
// =====================

export const WorkflowScopeSchema = z.object({
  moduleTypes: z.array(z.nativeEnum(ModuleTypeEnum)).optional(),
  moduleId: z.string().uuid().optional(),
})
export type WorkflowScope = z.infer<typeof WorkflowScopeSchema>

export const WorkflowSettingsSchema = z.object({
  scope: WorkflowScopeSchema.optional(),
  version: z.number().optional(),
  tags: z.array(z.string()).optional(),
})
export type WorkflowSettings = z.infer<typeof WorkflowSettingsSchema>

// =====================
// Trigger + Action Types
// =====================

export const WorkflowTriggerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  triggerType: WorkflowTriggerTypeEnum,
  config: z.record(z.unknown()),
  conditions: WorkflowConditionGroupSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  lastTriggered: z.date().nullable().optional(),
  triggerCount: z.number().default(0),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type WorkflowTrigger = z.infer<typeof WorkflowTriggerSchema>

export const WorkflowActionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  actionType: WorkflowActionTypeEnum,
  triggerTime: WorkflowActionTriggerTimeEnum,
  config: z.record(z.unknown()),
  conditions: WorkflowConditionGroupSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  lastExecuted: z.date().nullable().optional(),
  executionCount: z.number().default(0),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type WorkflowAction = z.infer<typeof WorkflowActionSchema>

export const WorkflowDefinitionSchema = z.object({
  triggers: z.array(WorkflowTriggerSchema),
  actions: z.array(WorkflowActionSchema),
})
export type WorkflowDefinition = z.infer<typeof WorkflowDefinitionSchema>

// =====================
// Run & Log Types
// =====================

export const WorkflowRunSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  organizationId: z.string().uuid(),
  status: WorkflowRunStatusEnum,
  triggerEventType: z.string().nullable().optional(),
  triggerModuleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  triggerModuleId: z.string().uuid().nullable().optional(),
  triggerPayload: z.record(z.unknown()).nullable().optional(),
  startedAt: z.date().nullable().optional(),
  completedAt: z.date().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type WorkflowRun = z.infer<typeof WorkflowRunSchema>

export const WorkflowRunStepSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  type: WorkflowRunStepTypeEnum,
  name: z.string(),
  status: WorkflowRunStepStatusEnum,
  order: z.number(),
  input: z.record(z.unknown()).nullable().optional(),
  output: z.record(z.unknown()).nullable().optional(),
  error: z.string().nullable().optional(),
  startedAt: z.date().nullable().optional(),
  completedAt: z.date().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type WorkflowRunStep = z.infer<typeof WorkflowRunStepSchema>

export const WorkflowRunLogSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  level: WorkflowLogLevelEnum,
  message: z.string(),
  data: z.record(z.unknown()).nullable().optional(),
  createdAt: z.date().optional(),
})
export type WorkflowRunLog = z.infer<typeof WorkflowRunLogSchema>

export const WorkflowRunDetailSchema = WorkflowRunSchema.extend({
  steps: z.array(WorkflowRunStepSchema).optional(),
  logs: z.array(WorkflowRunLogSchema).optional(),
})
export type WorkflowRunDetail = z.infer<typeof WorkflowRunDetailSchema>

// =====================
// Workflow Core Types
// =====================

export const WorkflowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: WorkflowStatusEnum,
  settings: WorkflowSettingsSchema.default({}),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type Workflow = z.infer<typeof WorkflowSchema>

export const WorkflowStatsSchema = z.object({
  triggerCount: z.number(),
  actionCount: z.number(),
  activeTriggerCount: z.number(),
  activeActionCount: z.number(),
})
export type WorkflowStats = z.infer<typeof WorkflowStatsSchema>

export const WorkflowListItemSchema = WorkflowSchema.extend({
  stats: WorkflowStatsSchema,
})
export type WorkflowListItem = z.infer<typeof WorkflowListItemSchema>

export const WorkflowDetailSchema = WorkflowSchema.extend({
  definition: WorkflowDefinitionSchema,
})
export type WorkflowDetail = z.infer<typeof WorkflowDetailSchema>

// =====================
// DTOs
// =====================

export const WorkflowTriggerInputSchema = z.object({
  name: z.string(),
  triggerType: WorkflowTriggerTypeEnum,
  config: z.record(z.unknown()).optional().default({}),
  conditions: WorkflowConditionGroupSchema.nullable().optional(),
  isActive: z.boolean().optional(),
})
export type WorkflowTriggerInput = z.infer<typeof WorkflowTriggerInputSchema>

export const WorkflowActionInputSchema = z.object({
  name: z.string(),
  actionType: WorkflowActionTypeEnum,
  triggerTime: WorkflowActionTriggerTimeEnum.optional().default('ON_TRIGGER'),
  config: z.record(z.unknown()).optional().default({}),
  conditions: WorkflowConditionGroupSchema.nullable().optional(),
  isActive: z.boolean().optional(),
})
export type WorkflowActionInput = z.infer<typeof WorkflowActionInputSchema>

export const WorkflowDefinitionInputSchema = z.object({
  triggers: z.array(WorkflowTriggerInputSchema).optional().default([]),
  actions: z.array(WorkflowActionInputSchema).optional().default([]),
})
export type WorkflowDefinitionInput = z.infer<
  typeof WorkflowDefinitionInputSchema
>

export const WorkflowEventInputSchema = z.object({
  eventType: z.string().min(1),
  moduleType: z.nativeEnum(ModuleTypeEnum).optional(),
  moduleId: z.string().uuid().optional(),
  actorId: z.string().uuid().optional(),
  payload: z.record(z.unknown()).optional(),
})
export type WorkflowEventInput = z.infer<typeof WorkflowEventInputSchema>

export const CreateWorkflowDtoSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  status: WorkflowStatusEnum.optional(),
  settings: WorkflowSettingsSchema.optional(),
  definition: WorkflowDefinitionInputSchema.optional(),
})
export type CreateWorkflowDto = z.infer<typeof CreateWorkflowDtoSchema>

export const UpdateWorkflowDtoSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  status: WorkflowStatusEnum.optional(),
  settings: WorkflowSettingsSchema.optional(),
  definition: WorkflowDefinitionInputSchema.optional(),
})
export type UpdateWorkflowDto = z.infer<typeof UpdateWorkflowDtoSchema>
